import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { auth } from '../config/firebase';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as { error?: string; message?: string };

      // Don't intercept auth endpoints - let the caller handle the error
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register') || requestUrl.includes('/password-recovery');

      switch (status) {
        case 401:
          if (!isAuthEndpoint && error.config && !(error.config as any)._retry) {
            // Try refreshing the token before logging out
            try {
              const user = auth.currentUser;
              if (user) {
                const newToken = await user.getIdToken(true);
                error.config.headers.Authorization = `Bearer ${newToken}`;
                (error.config as any)._retry = true;
                return api.request(error.config);
              }
            } catch {
              // Token refresh failed - proceed to logout
            }

            console.error('Unauthorized access - redirecting to login');
            sessionStorage.clear();
            const publicPaths = [
              '/login', '/signup', '/register', '/', '/landing', '/payments/result',
              '/loja/', '/ecommerce/store', '/ecommerce/checkout', '/ecommerce/bussiness',
              '/checkout', '/pay', '/evento/',
            ];
            const isPublicPage = publicPaths.some(path => window.location.pathname.startsWith(path))
              || window.location.pathname === '/ecommerce';
            if (!isPublicPage) {
              window.location.href = '/login';
            }
          }
          break;
        case 403:
          console.error('Forbidden access');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 429:
          console.error('Rate limit exceeded');
          break;
        case 500:
          console.error('Server error:', data.error);
          break;
        default:
          console.error('API error:', data.error);
      }

      throw new Error(data.error || data.message || 'An error occurred');
    } else if (error.request) {
      console.error('Network error - no response received');
      throw new Error('Erro de rede - verifique sua conexão');
    } else {
      console.error('Request error:', error.message);
      throw error;
    }
  }
);

export default api;

export { api, API_URL, API_VERSION };
